/**
 * HTTP-клиент для работы с API.
 * - x-trace-id для трассировки (Jaeger)
 * - Bearer token из localStorage
 * - 419: один refresh через POST /api/v1/token и один повтор запроса
 * - Отказ сессии (401 с кодом сессии, отказ refresh): повторный вход без потери
 *   открытых форм; 503 проверки сессии сессию не сбрасывает; 403 ACCOUNT_DISABLED — выход
 * - 5xx и мутации после повторного входа автоматически не повторяются
 * - Гостевые запросы (anonymous: вход, регистрация, сброс пароля, приглашение) идут без
 *   Authorization: токен прежней сессии не должен мешать открыть новую
 * - Токен убирается из хранилища, только когда сервер сказал, что сессии нет (401 с кодом
 *   повторного входа, 403 ACCOUNT_DISABLED, в том числе в ответе на refresh); временный
 *   отказ (429, 5xx, обрыв сети, ответ прокси) токен сохраняет: refresh-cookie может быть жива
 * - Обновление token из заголовка ответа
 * - Файлы (PDF, выгрузки) идут тем же путём (api.file): с маркером, обновлением сессии и отказом
 *   учебного режима без сети; ответ не того типа — ошибка, а не сохранённый файл
 * - Английский текст сервера без кода (в том числе отказ по праву) пользователю не
 *   показывается: ApiError.display несёт сообщение из словаря, деталь уходит в консоль
 */

import { PERMISSIONS } from '@/api/workspaceTypes';
import { sessionIdentity, useSessionStore } from '@/stores/session';
import {
	GENERIC_ERROR,
	REQUEST_UNAVAILABLE,
	isUntranslatedText,
	type KeyMessage,
} from '@/utils/displayMessage';

const BASE_URL = '/api';

// Уникальный trace-id для каждого запроса
function traceId(): string {
	if (crypto.randomUUID) {
		return crypto.randomUUID();
	}
	// Fallback для non-secure контекстов (HTTP dev-сервер)
	return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
		(+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16),
	);
}

/* ────────────────────────────────────────────
 * Работа с маркером доступа: хранение в localStorage браузера
 * ──────────────────────────────────────────── */

const TOKEN_KEY = 'Token';

function getToken(): string {
	return localStorage.getItem(TOKEN_KEY) ?? '';
}

function setToken(token: string | null) {
	if (token) {
		localStorage.setItem(TOKEN_KEY, token);
	}
}

function clearToken() {
	localStorage.removeItem(TOKEN_KEY);
}

/* ────────────────────────────────────────────
 * Token refresh (419 → POST /api/v1/token)
 * ──────────────────────────────────────────── */

let refreshPromise:
	| { token: string; identity: string | null; generation: number; promise: Promise<string> }
	| undefined;

/** Ответ старой сессии не имеет права изменять новую или повторять её действия */
function sessionSnapshot() {
	const token = getToken();
	const { user, generation } = useSessionStore.getState();
	const identity = sessionIdentity(user);
	return {
		token,
		identity,
		generation,
		isSameSession: () => generation === useSessionStore.getState().generation,
		isCurrent: () =>
			token === getToken() &&
			generation === useSessionStore.getState().generation &&
			identity === sessionIdentity(useSessionStore.getState().user),
	};
}

/**
 * Один refresh на все параллельные запросы. Ошибка несёт статус и код ответа:
 * отказ сервера (4xx, кроме 408/429) означает повторный вход, 5xx и сетевой сбой — временную
 * недоступность, при которой сессия не сбрасывается.
 */
function refreshToken(): Promise<string> {
	const snapshot = sessionSnapshot();
	if (
		refreshPromise?.token === snapshot.token &&
		refreshPromise.identity === snapshot.identity &&
		refreshPromise.generation === snapshot.generation
	)
		return refreshPromise.promise;

	const promise = fetch(`${BASE_URL}/v1/token`, {
		method: 'POST',
		credentials: 'include',
		headers: { 'x-trace-id': traceId() },
	})
		.catch(() => {
			throw new TokenRefreshError(0, null, 'Token refresh request failed');
		})
		.then(async (response) => {
			const newToken = response.headers.get('Token');
			if (!response.ok || !newToken) {
				const body = await response.json().catch(() => null);
				throw new TokenRefreshError(
					response.status,
					extractErrorCode(body),
					'Token refresh failed',
				);
			}
			if (!snapshot.isCurrent()) throw authorityUnavailableError();
			setToken(newToken);
			syncStoreToken(newToken);
			return newToken;
		})
		.finally(() => {
			if (refreshPromise?.promise === promise) refreshPromise = undefined;
		});

	refreshPromise = {
		token: snapshot.token,
		identity: snapshot.identity,
		generation: snapshot.generation,
		promise,
	};
	return promise;
}

// Синхронизация localStorage → Zustand store; повторный вход закрывает только вход
function syncStoreToken(token: string) {
	useSessionStore.getState().updateToken(token);
}

/* ────────────────────────────────────────────
 * Ошибки
 * ──────────────────────────────────────────── */

/** Только отказ самого fetch; тело/JSON/DTO сюда не попадают, raw cause не сохраняется. */
class FetchTransportError extends TypeError {
	constructor() {
		super('Failed to fetch');
		this.name = 'FetchTransportError';
	}
}

class ApiError extends Error {
	status: number;
	data?: unknown;
	/** Поле code тела ответа: числовой код gateway ("1036") или код сессии из контрактов §5 */
	code: string | null;
	/** Переводимое сообщение для ошибок, которые создаёт сам клиент */
	display?: KeyMessage;

	constructor(status: number, message: string, data?: unknown, display?: KeyMessage) {
		super(message);
		this.name = 'ApiError';
		this.status = status;
		this.data = data;
		this.code = extractErrorCode(data);
		this.display = display;
	}
}

/** Коды ошибок сессии (контракты §5) */
const SESSION_ERROR = {
	REAUTH_REQUIRED: 'SESSION_REAUTH_REQUIRED',
	ACCOUNT_DISABLED: 'ACCOUNT_DISABLED',
	AUTHORITY_UNAVAILABLE: 'SESSION_AUTHORITY_UNAVAILABLE',
} as const;

/** CREDENTIAL_REQUIRED gateway (ApiErrorCode, "1036"): refresh больше не принимается */
const LEGACY_CREDENTIAL_REQUIRED = '1036';

/** Коды 401, после которых нужен повторный вход, а не обычная ошибка формы */
const REAUTH_CODES: ReadonlySet<string> = new Set([
	SESSION_ERROR.REAUTH_REQUIRED,
	LEGACY_CREDENTIAL_REQUIRED,
]);

/** Истёкший access token при живой сессии (JwtAuthenticationFilter.STATUS_TOKEN_EXPIRED) */
const STATUS_TOKEN_EXPIRED = 419;

/** Отказ по праву (RequiresPermissionAspect): «Access denied: requires permission <право>» */
const PERMISSION_DENIED_TEXT = /^Access denied: requires permission (\S+)$/;

const KNOWN_PERMISSIONS: ReadonlySet<string> = new Set(PERMISSIONS);

/**
 * Сообщение из словаря вместо английского текста сервера. Без кода текст сервера -- это
 * текст исключения, а не контракт: отказ по праву называет право словарём, прочий 403 --
 * общим отказом доступа, остальные ошибки -- общим сообщением. Ответ с кодом и русский
 * текст не трогаются: их разбирает вызывающая страница. Право вне контракта сырым кодом
 * не показывается.
 */
function localizedFailure(
	status: number,
	code: string | null,
	message: string,
): KeyMessage | undefined {
	if (status >= 500) return REQUEST_UNAVAILABLE;
	if (code !== null || !isUntranslatedText(message)) return undefined;
	if (status !== 403) return GENERIC_ERROR;
	const permission = PERMISSION_DENIED_TEXT.exec(message)?.[1];
	return permission !== undefined && KNOWN_PERMISSIONS.has(permission)
		? { messageKey: 'common:errors.forbidden', params: { permission } }
		: { messageKey: 'common:errors.forbiddenRole' };
}

function safeDiagnosticCode(code: string | null): string {
	return code !== null && /^(?:[A-Z][A-Z0-9_]{0,63}|[0-9]{1,6})$/.test(code)
		? code
		: 'unclassified';
}

function extractErrorCode(body: unknown): string | null {
	if (body != null && typeof body === 'object' && 'code' in body) {
		const code = (body as { code?: unknown }).code;
		if (typeof code === 'string' && code !== '') {
			return code;
		}
	}
	return null;
}

/**
 * Текст ошибки для ApiError.message: приоритет — поле message разобранного
 * тела ответа (см. например DarSourceController.handleSourceConflict,
 * qhub-analytics: {status, error, message, path, ...} — тот же по форме
 * ответ отдают и другие обработчики бэкенда), а не response.statusText.
 * Причинная фраза протокола давно не приходит содержательной: HTTP/2+ её
 * вовсе не передаёт (браузер отдаёт пустую строку), поэтому любой код,
 * читающий err.message без разбора тела, получал бы пустую строку вместо
 * предметного текста сервера ("name is required" и т.п.), даже когда сервер
 * отвечает по существу. Пустая строка в message тела — тоже не текст:
 * тогда используется statusText, а если и он пуст — пустая строка остаётся
 * законным результатом (вызывающий код отвечает за запасной текст, см.
 * genericError в DataSourceForm.tsx).
 */
function extractErrorMessage(body: unknown, fallback: string): string {
	if (body != null && typeof body === 'object' && 'message' in body) {
		const message = (body as { message?: unknown }).message;
		if (typeof message === 'string' && message !== '') {
			return message;
		}
	}
	return fallback;
}

class TokenRefreshError extends Error {
	/** 0 — ответа нет (сетевой сбой) */
	status: number;
	code: string | null;

	constructor(status: number, code: string | null, message: string) {
		super(message);
		this.name = 'TokenRefreshError';
		this.status = status;
		this.code = code;
	}

	/** Сервер отказал в обновлении: нужен повторный вход */
	get rejected(): boolean {
		return this.status >= 400 && this.status < 500 && this.status !== 408 && this.status !== 429;
	}
}

/* ────────────────────────────────────────────
 * Query params
 * ──────────────────────────────────────────── */

type QueryParams = Record<string, string | number | boolean | undefined | null>;

function buildUrl(endpoint: string, params?: QueryParams): string {
	let url = `${BASE_URL}${endpoint}`;
	if (params) {
		const filtered = Object.entries(params).filter(
			([, v]) => v !== undefined && v !== null && v !== '',
		);
		if (filtered.length > 0) {
			const searchParams = new URLSearchParams(filtered.map(([k, v]) => [k, String(v)]));
			url += `?${searchParams.toString()}`;
		}
	}
	return url;
}

/* ────────────────────────────────────────────
 * Основная функция запроса
 * ──────────────────────────────────────────── */

interface RequestOptions extends Omit<RequestInit, 'body'> {
	params?: QueryParams;
	body?: unknown;
	/**
	 * Запрос гостя: Authorization не отправляется, 419 не запускает refresh. Для экранов,
	 * где сессии ещё нет: оставшийся от прежней сессии токен сервер отвергает 419 даже на
	 * открытых эндпоинтах, а refresh после этого отказывает, и войти нельзя.
	 */
	anonymous?: boolean;
	/**
	 * Ответ — файл этого типа (начало Content-Type): тело возвращается Blob. Ответ другого
	 * типа (страница ошибки прокси, JSON) — ошибка, а не файл с чужим содержимым
	 */
	file?: string;
}

/** Ошибка, после которой нужен повторный вход */
function reauthRequiredError(): ApiError {
	return new ApiError(
		401,
		'Session requires re-authentication',
		{ code: SESSION_ERROR.REAUTH_REQUIRED },
		{ messageKey: 'common:errors.reauthRequired' },
	);
}

/** Сессию нельзя проверить или обновить: сессия сохраняется, повтора нет */
function authorityUnavailableError(): ApiError {
	return new ApiError(
		503,
		'Session authority unavailable',
		{ code: SESSION_ERROR.AUTHORITY_UNAVAILABLE },
		{ messageKey: 'common:errors.sessionAuthorityUnavailable' },
	);
}

/**
 * Влияние ошибки на сессию. Повторный вход сохраняет пользователя и открытые
 * формы (E1.4); выход очищает их только при отключённой учётной записи.
 * Остальные ошибки, включая 401 без кода сессии (например, неверный текущий
 * пароль), остаются ошибками формы.
 */
function applySessionEffect(error: ApiError): void {
	if (requiresReauth(error)) {
		const session = useSessionStore.getState();
		session.requireReauth();
		/* Сервер сказал, что сессии нет: токен мёртв и в следующий вход не переносится */
		session.clearToken();
	} else if (isAccountDisabled(error)) {
		clearToken();
		useSessionStore.getState().clearSession();
	}
}

function requiresReauth(error: ApiError): boolean {
	return error.status === 401 && error.code !== null && REAUTH_CODES.has(error.code);
}

function isAccountDisabled(error: ApiError): boolean {
	return error.status === 403 && error.code === SESSION_ERROR.ACCOUNT_DISABLED;
}

/**
 * Ошибка закончила сессию (повторный вход или отключённая учётная запись): об этом
 * говорят окно входа или выход, отдельного уведомления об отказе действия не нужно.
 */
export function endsSession(error: unknown): boolean {
	return error instanceof ApiError && (requiresReauth(error) || isAccountDisabled(error));
}

/**
 * Сервер не знает сессию запроса: 401 с кодом повторного входа или без кода (токена нет
 * или он не принят). Для выхода это значит, что выход уже состоялся. 401 с другим кодом
 * (например, неверный пароль входа, 1029) сюда не относится.
 */
export function sessionUnknownToServer(error: unknown): boolean {
	return (
		error instanceof ApiError &&
		error.status === 401 &&
		(error.code === null || requiresReauth(error))
	);
}

/**
 * 419: не более одного refresh и одного повтора исходного запроса. Повтор
 * безопасен и для мутаций: 419 отдаёт фильтр до вызова контроллера.
 */
async function retryAfterRefresh<T>(
	endpoint: string,
	options: RequestOptions,
	refreshed: boolean,
): Promise<T> {
	/* После отказа refresh новые 419 не порождают новых попыток до повторного входа:
	   иначе каждый запрос и его повтор в React Query снова стучались бы в /token */
	if (refreshed || useSessionStore.getState().authState === 'reauth_required') {
		useSessionStore.getState().requireReauth();
		throw reauthRequiredError();
	}
	const snapshot = sessionSnapshot();
	try {
		const refreshedToken = await refreshToken();
		if (
			getToken() !== refreshedToken ||
			!snapshot.isSameSession() ||
			sessionIdentity(useSessionStore.getState().user) !== snapshot.identity
		)
			throw authorityUnavailableError();
	} catch (err) {
		if (!snapshot.isCurrent()) throw authorityUnavailableError();
		if (err instanceof TokenRefreshError && err.rejected) {
			/* Заблокированная учётная запись — выход, а не предложение войти снова */
			const refused = new ApiError(err.status, err.message, { code: err.code });
			applySessionEffect(refused);
			/* У гостя 401 refresh без кода тоже подтверждает отказ старого токена */
			if (!useSessionStore.getState().user && sessionUnknownToServer(refused)) {
				useSessionStore.getState().clearToken();
			}
			if (refused.code === SESSION_ERROR.ACCOUNT_DISABLED) {
				throw refused;
			}
			useSessionStore.getState().requireReauth();
			throw reauthRequiredError();
		}
		throw authorityUnavailableError();
	}
	return request<T>(endpoint, options, true);
}

/* ────────────────────────────────────────────
 * Маршрутизация учебного режима
 * ──────────────────────────────────────────── */

/** Запрос страницы, как его видит маршрутизатор учебного режима */
export interface RoutedRequest {
	method: string;
	endpoint: string;
	params?: QueryParams;
	body?: unknown;
}

/**
 * Маршрутизатор запросов (E4.5): в учебном режиме каждый запрос к данным рабочего
 * пространства отвечает учебный фасад или отказ без сети -- живой запрос не уходит.
 * null -- рабочий режим, запросы идут на сервер как есть.
 */
export type RequestRouter = (request: RoutedRequest) => Promise<unknown>;

let requestRouter: RequestRouter | null = null;

/**
 * Пути, которые в учебном режиме идут на сервер как есть: сессия и вход, признаки
 * возможностей и сам учебный API. Данных рабочего пространства среди них нет.
 */
const LIVE_IN_TRAINING = [
	/^\/v1\/(token|session|signin|signout|logout|features)\b/,
	/^\/v3\/training\//,
];

export function setRequestRouter(router: RequestRouter | null): void {
	requestRouter = router;
}

/** Учебный режим включён: запросы к данным идут через маршрутизатор */
export function isRequestRouted(): boolean {
	return requestRouter !== null;
}

/** Код отказа учебного режима: экрана или действия нет в учебном контуре */
export const TRAINING_UNAVAILABLE = 'training_view_unavailable';

/** Отказ без сети: в учебном контуре такого запроса нет, рабочие данные не запрашиваются */
export function trainingUnavailableError(): ApiError {
	return new ApiError(
		404,
		'Not available in training',
		{ code: TRAINING_UNAVAILABLE },
		{ messageKey: 'common:training.unavailable' },
	);
}

function routed(endpoint: string): boolean {
	return requestRouter !== null && !LIVE_IN_TRAINING.some((pattern) => pattern.test(endpoint));
}

async function request<T>(
	endpoint: string,
	options: RequestOptions = {},
	refreshed = false,
): Promise<T> {
	/* Сервер уже не принимает сессию: запросы страниц и мутации под окном повторного
	   входа не уходят на сервер и не повторяются после входа сами */
	if (useSessionStore.getState().authState === 'reauth_required') {
		throw reauthRequiredError();
	}

	if (routed(endpoint) && requestRouter) {
		/* Файлов в учебном контуре нет: учебный фасад отвечает данными, а не документами */
		if (options.file !== undefined) throw trainingUnavailableError();
		return (await requestRouter({
			method: (options.method ?? 'GET').toUpperCase(),
			endpoint,
			params: options.params,
			body: options.body,
		})) as T;
	}

	const {
		params,
		body,
		anonymous = false,
		file,
		headers: customHeaders,
		...fetchOptions
	} = options;

	const url = buildUrl(endpoint, params);

	const headers: Record<string, string> = {
		'Content-Type': 'application/json',
		'x-trace-id': traceId(),
		...(customHeaders as Record<string, string>),
	};

	// Auth token из localStorage; гостю он не отправляется
	const token = anonymous ? '' : getToken();
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const snapshot = sessionSnapshot();
	const requestBody = body !== undefined ? JSON.stringify(body) : undefined;
	let response: Response;
	try {
		response = await fetch(url, {
			...fetchOptions,
			headers,
			body: requestBody,
			credentials: 'include',
		});
	} catch (error) {
		if (
			fetchOptions.signal?.aborted ||
			(error !== null &&
				typeof error === 'object' &&
				'name' in error &&
				error.name === 'AbortError')
		)
			throw error;
		throw new FetchTransportError();
	}

	/* Ротация токена той же личности не обесценивает уже полученные данные.
	   Отказ или выдача токена старым запросом не могут менять новую сессию. */
	if (
		!anonymous &&
		(!snapshot.isSameSession() ||
			(!snapshot.isCurrent() &&
				(!response.ok ||
					response.headers.has('Token') ||
					snapshot.identity === null ||
					snapshot.identity !== sessionIdentity(useSessionStore.getState().user))))
	)
		throw authorityUnavailableError();

	if (!response.ok) {
		if (response.status === STATUS_TOKEN_EXPIRED && !anonymous) {
			return retryAfterRefresh<T>(endpoint, options, refreshed);
		}

		const errorData = await response.json().catch(() => null);
		if (!anonymous && !snapshot.isCurrent()) throw authorityUnavailableError();
		const message = extractErrorMessage(errorData, response.statusText);
		const display = localizedFailure(response.status, extractErrorCode(errorData), message);
		if (display || extractErrorCode(errorData) !== null) {
			console.warn(
				'API request failed:',
				response.status,
				(fetchOptions.method ?? 'GET').toUpperCase(),
				endpoint.split('?')[0],
				response.status >= 500 ? 'server' : 'request',
				safeDiagnosticCode(extractErrorCode(errorData)),
			);
		}
		const error = new ApiError(response.status, message, errorData, display);
		if (!anonymous) {
			applySessionEffect(error);
			if (
				endpoint === '/v1/session' &&
				!useSessionStore.getState().user &&
				sessionUnknownToServer(error)
			)
				useSessionStore.getState().clearToken();
		}
		throw error;
	}

	// Обновление token из заголовка ответа
	const newToken = response.headers.get('Token');
	if (newToken) {
		setToken(newToken);
		syncStoreToken(newToken);
	}

	if (file !== undefined) {
		const type = response.headers.get('Content-Type') ?? '';
		if (!type.startsWith(file)) {
			throw new ApiError(
				response.status,
				'Unexpected content type',
				{ content_type: type },
				GENERIC_ERROR,
			);
		}
		const blob = await response.blob();
		if (!anonymous && !snapshot.isSameSession()) throw authorityUnavailableError();
		return blob as T;
	}

	// 204 No Content
	if (response.status === 204) {
		return undefined as T;
	}

	/* Часть эндпоинтов отвечает 200 без тела (смена пароля, регистрация с подтверждением
	   email): это успех без данных, а не ошибка разбора JSON */
	const text = await response.text();
	if (!anonymous && !snapshot.isSameSession()) throw authorityUnavailableError();
	return (text === '' ? undefined : JSON.parse(text)) as T;
}

/* ────────────────────────────────────────────
 * Публичный API
 * ──────────────────────────────────────────── */

export const api = {
	/** Запрос с именованными параметрами: гостевые вызовы не требуют пустых позиций */
	request: <T>(url: string, options: RequestOptions) => request<T>(url, options),

	get: <T>(url: string, params?: QueryParams, options?: Omit<RequestOptions, 'params'>) =>
		request<T>(url, { ...options, params, method: 'GET' }),

	post: <T>(
		url: string,
		body?: unknown,
		params?: QueryParams,
		options?: Pick<RequestOptions, 'anonymous'>,
	) => request<T>(url, { ...options, body, params, method: 'POST' }),

	put: <T>(url: string, body?: unknown, params?: QueryParams) =>
		request<T>(url, { body, params, method: 'PUT' }),

	patch: <T>(url: string, body?: unknown, params?: QueryParams) =>
		request<T>(url, { body, params, method: 'PATCH' }),

	delete: <T>(url: string, params?: QueryParams) => request<T>(url, { params, method: 'DELETE' }),

	/** Файл ожидаемого типа (Blob): та же авторизация и обновление сессии, что у данных */
	file: (url: string, type: string, options?: { params?: QueryParams; signal?: AbortSignal }) =>
		request<Blob>(url, {
			params: options?.params,
			signal: options?.signal,
			method: 'GET',
			file: type,
		}),
};

export {
	ApiError,
	FetchTransportError,
	SESSION_ERROR,
	TokenRefreshError,
	authorityUnavailableError,
	getToken,
	setToken,
	clearToken,
};
export type { QueryParams };
